import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { fmtEUR, fmtPct, fmtNum } from "@/lib/format";
import { Sprout } from "lucide-react";

/**
 * Print-only report layout - clean white background for PDF export.
 * Visible only when @media print or has .force-print class.
 */
export const PrintReport = ({ forceVisible = false }) => {
  const { inputs, summary, projection, activeScenarioName } = useSimulator();

  return (
    <div
      className={`print-page print-only bg-white text-slate-900 p-10 ${forceVisible ? "!block" : ""}`}
      data-testid="print-report"
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-600 text-white">
            <Sprout className="h-6 w-6" />
          </div>
          <div>
            <div className="text-2xl font-extrabold font-display tracking-tight">H2 Farm</div>
            <div className="text-xs text-slate-600 uppercase tracking-wider">
              Simulazione Economica — Report Investitori
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-slate-600">
          <div className="font-semibold">{activeScenarioName}</div>
          <div>{new Date().toLocaleDateString("it-IT")}</div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <PrintKpi label="Ricavo Lordo" value={fmtEUR(summary.grossRevenue)} className="print-accent-emerald" />
        <PrintKpi label="OPEX Annuo" value={fmtEUR(summary.opex.annual)} className="print-accent-rose" />
        <PrintKpi label="Margine Op." value={`${fmtEUR(summary.operatingMargin)} (${fmtPct(summary.marginPct)})`} />
        <PrintKpi
          label="Break-Even/mese"
          value={summary.breakevenMonthlyRevenue ? fmtEUR(summary.breakevenMonthlyRevenue) : "N/D"}
          className="print-accent-amber"
        />
      </div>

      {/* Lotto 1 */}
      <Section title="Lotto 1 — Zafferano Aeroponico">
        <div className="grid grid-cols-2 gap-4">
          <Row label="Produzione annua totale" value={`${fmtNum(inputs.saffron.kg_year, 2)} kg`} />
          <Row label="Ricavo totale Lotto 1" value={fmtEUR(summary.saffron.total)} bold />
          <Row label={`Stimmi essiccati (${inputs.saffron.mix_dried}% raw)`} value={`${fmtNum(summary.saffron.kg_dried, 3)} kg · ${fmtEUR(summary.saffron.rev_dried)}`} />
          <Row label={`Estratto std. (${inputs.saffron.mix_extract}% × resa ${inputs.saffron.yield_extract}%)`} value={`${fmtNum(summary.saffron.kg_extract, 3)} kg · ${fmtEUR(summary.saffron.rev_extract)}`} />
          <Row label={`Estratto SC-CO₂ (${inputs.saffron.mix_scco2}% × resa ${inputs.saffron.yield_scco2}%)`} value={`${fmtNum(summary.saffron.kg_scco2, 3)} kg · ${fmtEUR(summary.saffron.rev_scco2)}`} />
        </div>
      </Section>

      {/* Lotto 2 */}
      <Section title="Lotto 2 — Microalghe in Fotobioreattori">
        <div className="grid grid-cols-3 gap-4">
          {["chlorella", "spirulina", "scenedesmus"].map((key) => {
            const sp = inputs.algae[key];
            const r = summary.algae[key];
            return (
              <div key={key} className="print-card border border-slate-300 rounded-md p-3">
                <div className="font-semibold text-slate-800">{sp.name}</div>
                <div className="mt-1 text-xs space-y-0.5">
                  <div>Biomassa: {fmtNum(sp.kg_year)} kg</div>
                  <div>Essiccato ({sp.yield_dried}%): {fmtEUR(r.rev_dried)}</div>
                  <div>SC-CO₂ ({sp.yield_scco2}%): {fmtEUR(r.rev_scco2)}</div>
                  <div className="pt-1 font-semibold border-t border-slate-300 mt-1">
                    Totale: {fmtEUR(r.total)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* OPEX */}
      <Section title="Costi Operativi Mensili (OPEX)">
        <div className="grid grid-cols-2 gap-4">
          <Row label="Energia" value={`${fmtEUR(inputs.opex.energy)} / mese`} />
          <Row label="Consumabili" value={`${fmtEUR(inputs.opex.consumables)} / mese`} />
          <Row label="Manodopera" value={`${fmtEUR(inputs.opex.labor)} / mese`} />
          <Row label="Manutenzione" value={`${fmtEUR(inputs.opex.maintenance)} / mese`} />
          <Row label="OPEX mensile totale" value={fmtEUR(summary.opex.monthly)} bold />
          <Row label="OPEX annuo totale (Lotti 1-2)" value={fmtEUR(summary.opex.annual)} bold />
          <Row label="OPEX annuo lotti futuri" value={fmtEUR(summary.futureLots.opex)} />
          <Row label="OPEX annuo COMPLESSIVO" value={fmtEUR(summary.totalOpexAnnual)} bold />
        </div>
      </Section>

      {/* CAPEX & Payback */}
      <Section title="CAPEX, Payback & IRR">
        <div className="grid grid-cols-2 gap-4">
          <Row label="CAPEX iniziale Lotti 1-2" value={fmtEUR(inputs.capex?.initial_investment || 0)} />
          <Row label="CAPEX lotti futuri" value={fmtEUR(summary.futureLots.capex)} />
          <Row label="CAPEX totale" value={fmtEUR(summary.totalCapex)} bold />
          <Row label="ROI 5 anni (semplice)" value={`${summary.roi5y.toFixed(1)} %`} />
        </div>
      </Section>

      {/* Future lots */}
      {summary.futureLots.count > 0 && (
        <Section title={`Lotti Futuri Attivi (${summary.futureLots.count})`}>
          <div className="grid grid-cols-2 gap-3">
            {(inputs.future_lots || []).filter((l) => l.enabled).map((l) => (
              <div key={l.id} className="print-card border border-slate-300 rounded-md p-3">
                <div className="font-semibold text-slate-800">{l.name}</div>
                <div className="text-xs text-slate-600">{l.description}</div>
                <div className="mt-1 text-xs space-y-0.5">
                  <div>Superficie: {l.surface_m2} m²</div>
                  <div>Ricavo: {fmtEUR(l.annual_revenue)}</div>
                  <div>OPEX: {fmtEUR(l.annual_opex)}</div>
                  <div className="pt-1 font-semibold border-t border-slate-300 mt-1">
                    EBITDA: {fmtEUR(l.annual_revenue - l.annual_opex)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Projection Table */}
      <Section title={`Proiezione a 5 Anni (Reinvestimento ${inputs.projection.reinvestment_pct}%)`}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-800">
              <th className="text-left py-2 px-2">Anno</th>
              <th className="text-right py-2 px-2">Capacità</th>
              <th className="text-right py-2 px-2">Ricavo</th>
              <th className="text-right py-2 px-2">OPEX</th>
              <th className="text-right py-2 px-2">Margine</th>
              <th className="text-right py-2 px-2">Reinvestito</th>
              <th className="text-right py-2 px-2">Cassa Cum.</th>
            </tr>
          </thead>
          <tbody>
            {projection.map((row) => (
              <tr key={row.year} className="border-b border-slate-200">
                <td className="py-1.5 px-2 font-semibold">Anno {row.year}</td>
                <td className="text-right py-1.5 px-2">{fmtNum(row.capacity, 3)}×</td>
                <td className="text-right py-1.5 px-2">{fmtEUR(row.revenue)}</td>
                <td className="text-right py-1.5 px-2">{fmtEUR(row.opex)}</td>
                <td className="text-right py-1.5 px-2">{fmtEUR(row.margin)}</td>
                <td className="text-right py-1.5 px-2">{fmtEUR(row.reinvested)}</td>
                <td className="text-right py-1.5 px-2 font-semibold">{fmtEUR(row.cumulativeCash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <div className="mt-8 pt-4 border-t border-slate-300 text-[10px] text-slate-500 text-center">
        H2 Farm · Report generato il {new Date().toLocaleString("it-IT")} · Simulatore Economico v1.0
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-6 break-inside-avoid">
    <h2 className="font-display text-lg font-bold text-slate-800 border-b border-slate-300 pb-1 mb-3">
      {title}
    </h2>
    {children}
  </div>
);

const Row = ({ label, value, bold = false }) => (
  <div className={`flex justify-between text-xs py-1 ${bold ? "font-bold border-t border-slate-400 pt-2 mt-1" : ""}`}>
    <span className="text-slate-700">{label}</span>
    <span className="font-mono-tab text-slate-900">{value}</span>
  </div>
);

const PrintKpi = ({ label, value, className = "" }) => (
  <div className="print-card border-2 border-slate-800 rounded-md p-3">
    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-600">{label}</div>
    <div className={`mt-1 font-display font-extrabold text-sm ${className}`}>{value}</div>
  </div>
);
